import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeSiblingCompanyMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    type_sibling: { value: '', error: false, helperText: '' },
    name_of_company: { value: '', error: false, helperText: '' },
    address: { value: '', error: false, helperText: '' },
    phone_no: { value: '', error: false, helperText: '' },
    email: { value: '', error: false, helperText: '' },
    director_name: { value: '', error: false, helperText: '' },
    directors_phone_no: { value: '', error: false, helperText: '' },
    director_email: { value: '', error: false, helperText: '' },
    direct_address: { value: '', error: false, helperText: '' },
  });

  const [uploading, setUploading] = useState(false);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 150 },
    { id: 'type_sibling', field: 'type_sibling', headerName: 'Sibling Type', width: 150 },
    { id: 'name_of_company', field: 'name_of_company', headerName: 'Company Name', width: 180 },
    { id: 'address', field: 'address', headerName: 'Address', width: 250 },
    { id: 'phone_no', field: 'phone_no', headerName: 'Phone Number', width: 150 },
    { id: 'email', field: 'email', headerName: 'Email', width: 200 },
    { id: 'director_name', field: 'director_name', headerName: 'Director Name', width: 180 },
    { id: 'directors_phone_no', field: 'directors_phone_no', headerName: 'Director Phone', width: 160 },
    { id: 'director_email', field: 'director_email', headerName: 'Director Email', width: 200 },
    { id: 'direct_address', field: 'direct_address', headerName: 'Director Address', width: 250 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchSiblingCompanies = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-sibling-company/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            type_sibling: item.type_sibling,
            name_of_company: item.name_of_company,
            address: item.address,
            phone_no: item.phone_no,
            email: item.email,
            director_name: item.director_name,
            directors_phone_no: item.directors_phone_no,
            director_email: item.director_email,
            direct_address: item.direct_address,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load sibling companies.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) fetchSiblingCompanies();
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      type_sibling: { value: '', error: false, helperText: '' },
      name_of_company: { value: '', error: false, helperText: '' },
      address: { value: '', error: false, helperText: '' },
      phone_no: { value: '', error: false, helperText: '' },
      email: { value: '', error: false, helperText: '' },
      director_name: { value: '', error: false, helperText: '' },
      directors_phone_no: { value: '', error: false, helperText: '' },
      director_email: { value: '', error: false, helperText: '' },
      direct_address: { value: '', error: false, helperText: '' },
    });
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      type_sibling: { value: item.type_sibling, error: false, helperText: '' },
      name_of_company: { value: item.name_of_company, error: false, helperText: '' },
      address: { value: item.address, error: false, helperText: '' },
      phone_no: { value: item.phone_no, error: false, helperText: '' },
      email: { value: item.email, error: false, helperText: '' },
      director_name: { value: item.director_name, error: false, helperText: '' },
      directors_phone_no: { value: item.directors_phone_no, error: false, helperText: '' },
      director_email: { value: item.director_email, error: false, helperText: '' },
      direct_address: { value: item.direct_address, error: false, helperText: '' },
    });
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-sibling-company/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Sibling company deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchSiblingCompanies();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete sibling company.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateField = (name, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      switch (name) {
        case 'financial_establishment': return 'FE Base is required';
        case 'type_sibling': return 'Sibling type is required';
        case 'name_of_company': return 'Company name is required';
        case 'address': return 'Address is required';
        case 'phone_no': return 'Phone number is required';
        case 'email': return 'Email is required';
        case 'director_name': return 'Director name is required';
        case 'directors_phone_no': return 'Director phone is required';
        case 'director_email': return 'Director email is required';
        case 'direct_address': return 'Director address is required';
        default: return '';
      }
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errMsg = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: { value, error: !!errMsg, helperText: errMsg },
    }));
  };

  const validateForm = () => {
    let valid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach(name => {
      const errMsg = validateField(name, newFields[name].value);
      if (errMsg) valid = false;
      newFields[name] = { ...newFields[name], error: !!errMsg, helperText: errMsg };
    });
    setFormFields(newFields);
    return valid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {};
    Object.keys(formFields).forEach(key => {
      payload[key] = formFields[key].value;
    });

    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-sibling-company/${selectedItem.id}/`, payload);
        setSnackbarMessage('Sibling company updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-sibling-company/`, payload);
        setSnackbarMessage('Sibling company added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSiblingCompanies();
      setOpenFormDialog(false);
    } catch {
      setSnackbarMessage('Failed to submit sibling company.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Sibling Companies</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>

      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }} />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Sibling Company' : 'Add Sibling Company'}</DialogTitle>
        <DialogContent>
          {Object.entries(formFields).map(([field, { value, error, helperText }]) => {
            if (field === 'financial_establishment') {
              return (
                <TextField key={field} select margin="dense" label="FE Base" fullWidth name={field}
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={handleInputChange}
                  disabled={uploading}>
                  {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
                </TextField>
              );
            }
            else if (field === 'address' || field === 'direct_address') {
              return (
                <TextField key={field} margin="dense" label={field.split('_').join(' ').replace(/\b\w/g, c => c.toUpperCase())} fullWidth multiline minRows={3} name={field}
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={handleInputChange}
                  disabled={uploading} />
              );
            }
            else if (field === 'phone_no' || field === 'directors_phone_no') {
              return (
                <TextField key={field} margin="dense" label={field.split('_').join(' ').replace(/\b\w/g, c => c.toUpperCase())} fullWidth type="tel" name={field}
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={handleInputChange}
                  disabled={uploading} />
              );
            }
            else if (field === 'email' || field === 'director_email') {
              return (
                <TextField key={field} margin="dense" label={field.split('_').join(' ').replace(/\b\w/g, c => c.toUpperCase())} fullWidth type="email" name={field}
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={handleInputChange}
                  disabled={uploading} />
              );
            }
            else {
              return (
                <TextField key={field} margin="dense" label={field.split('_').join(' ').replace(/\b\w/g, c => c.toUpperCase())} fullWidth name={field}
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={handleInputChange}
                  disabled={uploading} />
              );
            }
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Sibling Company?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
